<!-- Blog Listing page allows user to search blog posts using categories and tags;
     returns cards with links to the relevant posts. -->

<template>
  <main class="pad">
    <div v-if="!loading">
      <!--Parallax image with logo, title, and homepage link (if department BlogListing)-->

      <PageHeader
        v-if="
          bloglisting.meta.parent.meta.type ==
          'departments.DepartmentLandingPage'
        "
        :title="bloglisting.title"
        :logo_url="
          bloglisting.logo
            ? bloglisting.logo.meta.download_url
            : bloglisting.get_department_logo_url
        "
        :image_url="
          bloglisting.image
            ? bloglisting.image.meta.download_url
            : bloglisting.get_department_image_url
        "
        :subtitle="bloglisting.subtitle ? bloglisting.subtitle : ''"
        :dept_link="'/departmentlandingpage/' + parentSlug"
      >
      </PageHeader>

      <PageHeader
        v-else
        :title="bloglisting.title"
        :logo_url="
          bloglisting.logo
            ? bloglisting.logo.meta.download_url
            : bloglisting.get_homepage_logo_url
        "
        :image_url="
          bloglisting.image
            ? bloglisting.image.meta.download_url
            : bloglisting.get_homepage_image_url
        "
        :subtitle="bloglisting.subtitle ? bloglisting.subtitle : ''"
      >
      </PageHeader>

      <!--Search field-->

      <mdb-container>
        <div class="input-group md-form form-sm form-1 pl-0">
          <div class="input-group-prepend">
            <span class="input-group-text lighten-3" id="basic-text1">
              <mdbIcon icon="search" />
            </span>
          </div>
          <input
            class="form-control my-0 py-1"
            id="search_bar"
            type="text"
            placeholder="Search"
            v-model="searchQuery"
            aria-label="Search"
          />
        </div>

        <!--All posts returned by search (or displayed normally)-->

        <mdb-container>
          <mdb-row>
            <mdb-col>
              <BlogPosts
                v-if="!loading"
                :descendant_of="descendant_of"
                :tag="selectedTag"
                :category="selectedCategory"
                :search="safeReturnSearch"
                :type="page_type"
                @setUpPagination="setUpPagination"
                :offset="offset"
              ></BlogPosts>
            </mdb-col>

            <!--Pagination indicators ONLY on small mobile-->

            <mdb-col v-if="num_pages > 1" class="d-md-none d-sm-block mb-5">
              <mdb-pagination lg>
                <mdb-page-nav
                  prev
                  @click.native="setOffset(parseInt(current_pg) - 1)"
                  :disabled="current_pg == 1"
                ></mdb-page-nav>
                <mdb-page-item
                  v-for="(page, pg_num) in pg_ary"
                  :key="pg_num"
                  :active="current_pg == page"
                  @click.native="setOffset(page)"
                  >{{ page }}</mdb-page-item
                >
                <mdb-page-nav
                  next
                  @click.native="setOffset(parseInt(current_pg) + 1)"
                  :disabled="current_pg == num_pages"
                ></mdb-page-nav>
              </mdb-pagination>
            </mdb-col>

            <!--Post Categories-->

            <mdb-col md="4" v-if="categories.length != 0 || tags.length != 0">
              <mdb-card class="mb-3 mt-3 ml-3" v-if="categories.length != 0">
                <mdb-card-header>
                  <strong>Categories</strong>
                </mdb-card-header>
                <mdb-card-body>
                  <mdb-card-text>
                    <mdb-list-group>
                      <mdb-mask waves>
                        <mdb-list-group-item
                          v-for="category in categories"
                          :key="category.id"
                          action
                          class="my-1"
                          :class="{ active: category.id === selectedCategory }"
                          @click.native="updateCategory(category.id)"
                          >{{ category.name }}</mdb-list-group-item
                        >
                      </mdb-mask>
                    </mdb-list-group>
                  </mdb-card-text>
                </mdb-card-body>
              </mdb-card>

              <!--Post Tags-->

              <mdb-card
                class="mb-3 ml-3 justify-content-center"
                v-if="tags.length != 0"
              >
                <mdb-card-header>
                  <strong>Tags</strong>
                </mdb-card-header>
                <mdb-card-body>
                  <mdb-card-text>
                    <mdb-chip
                      v-for="(tag, index) in tags"
                      :key="index"
                      action
                      @click.native="updateTag(tag.name)"
                      :class="{ 'primary-color': tag.name === selectedTag }"
                    >
                      <mdb-icon icon="hashtag" />
                      {{ tag.name }}
                    </mdb-chip>
                  </mdb-card-text>
                </mdb-card-body>
              </mdb-card>
              <mdb-btn
                v-if="selectedCategory || selectedTag"
                icon="times-circle"
                @click.native="clearFilters('all')"
                block
                color="secondary"
                class="pt-1 pb-1 ml-2 mt-4"
                >Clear Filters</mdb-btn
              >
            </mdb-col>
          </mdb-row>

          <!--Pagination indicators (only show up if there is necessary pagination.-->

          <mdb-row v-if="num_pages > 1" class="d-none d-md-block">
            <mdb-col>
              <mdb-pagination lg>
                <mdb-page-nav
                  prev
                  @click.native="setOffset(parseInt(current_pg) - 1)"
                  :disabled="current_pg == 1"
                ></mdb-page-nav>
                <mdb-page-item
                  v-for="(page, pg_num) in pg_ary"
                  :key="pg_num"
                  :active="current_pg == page"
                  @click.native="setOffset(page)"
                  >{{ page }}</mdb-page-item
                >
                <mdb-page-nav
                  next
                  @click.native="setOffset(parseInt(current_pg) + 1)"
                  :disabled="current_pg == num_pages"
                ></mdb-page-nav>
              </mdb-pagination>
            </mdb-col>
          </mdb-row>
        </mdb-container>
      </mdb-container>
    </div>
  </main>
</template>

<script>
import {
  mdbContainer,
  mdbRow,
  mdbCol,
  mdbBtn,
  mdbIcon,
  mdbChip,
  mdbCard,
  mdbCardBody,
  mdbCardHeader,
  mdbCardText,
  mdbListGroup,
  mdbListGroupItem,
  mdbMask,
  mdbPagination,
  mdbPageNav,
  mdbPageItem,
} from "mdbvue";
import axios from "axios";
import api from "@/api";
import BlogPosts from "@/views/blog/BlogPosts.vue";
import PageHeader from "@/views/PageHeader.vue";
import shared from "@/shared";
export default {
  name: "BlogListing",
  components: {
    mdbContainer,
    mdbRow,
    mdbCol,
    mdbBtn,
    mdbIcon,
    mdbChip,
    mdbCard,
    mdbCardBody,
    mdbCardHeader,
    mdbCardText,
    mdbListGroup,
    mdbListGroupItem,
    mdbMask,
    mdbPagination,
    mdbPageNav,
    mdbPageItem,
    BlogPosts,
    PageHeader,
  },
  props: {
    slug: {
      type: [String],
    },
    type: {
      type: [String],
      default: "blog.BlogDetailPage",
    },
  },
  data() {
    return {
      loading: true,
      descendant_of: false,
      categories: false,
      tags: false,
      bloglisting: false,
      selectedCategory: false,
      selectedTag: false,
      searchQuery: "",
      parentSlug: "",
      page_type: "blog.DepartmentArticleBlogPage",
      tag_color: "grey",
      category_color: "grey",
      offset: 0,
      current_pg: 1,
      pg_ary: [],
      num_pages: 0,
    };
  },
  computed: {
    axiosParams() {
      const params = new URLSearchParams();
      params.append("fields", "*");
      return params;
    },
    safeReturnSearch() {
      //TODO currently for some reason when cat/tags are filtered in rest api search opeartor doesnt work
      if (this.searchQuery.trim()) {
        this.clearFilters("all");
      }
      return this.searchQuery.trim();
    },
  },
  methods: {
    getData() {
      axios;
      api
        .get("pages/" + this.slug, {
          params: this.axiosParams,
        })
        .then((response) => {
          this.bloglisting = response.data;
          this.descendant_of = this.bloglisting.id;
          this.categories = this.bloglisting.get_all_categories;
          this.tags = this.bloglisting.get_child_tags;
          shared.getDepartmentSkinFromPage(this.bloglisting);
          this.parentSlug = shared.extractParentSlug(
            this.bloglisting.meta.parent.meta.html_url
          );
          if (
            this.bloglisting.meta.parent.meta.type !=
            "departments.DepartmentLandingPage"
          ) {
            this.page_type = "blog.BlogDetailPage";
          }
        })
        .catch((e) => {
          this.errors.push(e);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    setUpPagination(count) {
      /* If num posts > 10, paginate by 10 otherwise show all on one page */

      if (count == 0) {
        this.pg_ary = null;
        this.num_pages = 0;
        return;
      } else {
        this.num_pages = count / 10;
        this.num_pages = Math.ceil(this.num_pages);
      }
      var pg_num_ary = [];
      for (var i = 0; i < this.num_pages; i++) {
        pg_num_ary[i] = (i + 1).toString();
      }
      this.pg_ary = pg_num_ary;
      //this.offset = 0;
    },
    setOffset(pg_num) {
      /* Offset ensures proper pagination */
      if (pg_num > 0) {
        if (pg_num == 1) {
          this.offset = 0;
        } else {
          this.offset = 10 * (pg_num - 1);
        }
        this.current_pg = pg_num;
      }
    },
    updateCategory(catNum) {
      this.clearSearch();
      if (catNum == this.selectedCategory) {
        this.selectedCategory = false;
      } else {
        this.selectedCategory = catNum;
      }

      /* Additionally, return user to top of page for results. */

      var search_bar = document.getElementById("search_bar");
      search_bar.scrollIntoView({ behavior: "smooth" });
    },
    updateTag(tagName) {
      this.clearSearch();
      if (tagName == this.selectedTag) {
        this.selectedTag = false;
      } else {
        this.selectedTag = tagName;
      }

      /* Additionally, return user to top of page for results. */

      var search_bar = document.getElementById("search_bar");
      search_bar.scrollIntoView({ behavior: "smooth" });
    },
    clearFilters(item) {
      if (item === "tag" || item === "all") {
        this.selectedTag = false;
        this.tag_color = "grey";
        this.category_color = "grey";
      }
      if (item === "cat" || item === "all") {
        this.selectedCategory = false;
      }
    },
    clearSearch() {
      this.searchQuery = "";
    },
  },
  watch: {
    slug: function () {
      // watch it
      this.clearFilters("all");
      this.clearSearch();
      this.getData();
    },
  },
  mounted() {
    this.getData();
  },
};
</script>
